import { useState, type FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';
import PrivacyPolicyModal from '../../../components/feature/PrivacyPolicyModal';

export default function ContactSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>();
  const { ref: decorRef, style: decorStyle } = useParallax<HTMLDivElement>({ speed: 0.18, direction: 'down' });

  const [formData, setFormData] = useState({
    name: '',
    emailPhone: '',
    message: '',
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);

  const handleChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.name || !formData.emailPhone || !formData.consent) {
      setSubmitStatus('error');
      return;
    }
    if (formData.message.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://pfheywmspfdagrktvcvk.supabase.co/functions/v1/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          emailPhone: formData.emailPhone,
          message: formData.message,
          consent: formData.consent,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      await response.json();

      setSubmitStatus('success');
      setFormData({ name: '', emailPhone: '', message: '', consent: false });
    } catch (err) {
      console.error('Form submission failed:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="bg-dark-light py-16 sm:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden">
      {/* Parallax Decorative Elements */}
      <div ref={decorRef} style={decorStyle} className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 right-1/3 w-96 h-96 bg-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 left-1/4 w-80 h-80 bg-gold/4 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div
          className={`text-center mb-8 sm:mb-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="w-16 h-0.5 bg-gold mx-auto mb-6 sm:mb-8"></div>
          <h2 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            <a href="#contact">
              <strong>Готовы навести порядок в управлении персоналом?</strong>
            </a>
          </h2>
          <p className="font-body text-sm text-white/50 italic px-2">
            Опишите вашу задачу, и я подготовлю предварительный разбор ситуации.
          </p>
        </div>

        <form
          id="consultation-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className={`space-y-5 sm:space-y-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: isVisible ? '200ms' : '0ms' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <input
                type="text"
                name="name"
                placeholder="Имя"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-dark border border-white/15 rounded-md font-body text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/60 transition-colors"
              />
            </div>
            <div>
              <input
                type="text"
                name="email"
                placeholder="Email/Телефон"
                value={formData.emailPhone}
                onChange={(e) => handleChange('emailPhone', e.target.value)}
                required
                className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-dark border border-white/15 rounded-md font-body text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/60 transition-colors"
              />
            </div>
          </div>

          <div>
            <textarea
              name="message"
              placeholder="Краткое описание задачи"
              value={formData.message}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  handleChange('message', e.target.value);
                }
              }}
              maxLength={500}
              rows={4}
              className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-dark border border-white/15 rounded-md font-body text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/60 transition-colors resize-none"
            />
            <p className="font-body text-xs text-white/30 mt-1 text-right">
              {formData.message.length}/500
            </p>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="consent"
              name="consent"
              checked={formData.consent}
              onChange={(e) => handleChange('consent', e.target.checked)}
              className="mt-1 accent-gold cursor-pointer"
            />
            <label
              htmlFor="consent"
              className="font-body text-xs text-white/50 cursor-pointer"
            >
              Согласен(а) на обработку персональных данных в соответствии с{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setIsPolicyOpen(true);
                }}
                className="text-gold hover:text-gold-dark underline transition-colors cursor-pointer"
              >
                политикой конфиденциальности
              </button>
              .
            </label>
          </div>

          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting || !formData.consent}
              className="w-full sm:w-auto px-12 py-3 sm:py-4 bg-gold hover:bg-gold-dark disabled:opacity-50 text-dark font-body font-semibold text-sm rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Отправка...' : 'Получите'}
            </button>
          </div>

          {submitStatus === 'success' && (
            <div className="text-center">
              <p className="font-body text-sm text-green-400">
                Спасибо! Ваша заявка отправлена. Мы свяжемся с вами в ближайшее время.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="text-center">
              <p className="font-body text-sm text-red-400">
                Произошла ошибка. Пожалуйста, попробуйте ещё раз.
              </p>
            </div>
          )}
        </form>
      </div>

      <PrivacyPolicyModal isOpen={isPolicyOpen} onClose={() => setIsPolicyOpen(false)} />
    </section>
  );
}
